import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { v2Api } from '@/lib/api';
import type { ExerciseTeamMapping, ExerciseWalkthrough } from '@/lib/api/v2';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';

interface WalkthroughsCardProps {
  exerciseId: string;
  title?: string;
  initialItems?: ExerciseWalkthrough[];
  initialMappings?: ExerciseTeamMapping[];
  onRefetch?: () => Promise<unknown>;
}

type FormState = {
  title: string;
  exercise_team: number;
  is_active: boolean;
  file: File | null;
};

const emptyForm: FormState = {
  title: '',
  exercise_team: 0,
  is_active: true,
  file: null,
};

export function WalkthroughsCard({
  exerciseId,
  title = 'Walkthroughs',
  initialItems,
  initialMappings,
  onRefetch,
}: WalkthroughsCardProps) {
  const [items, setItems] = useState<ExerciseWalkthrough[]>(initialItems ?? []);
  const [mappings, setMappings] = useState<ExerciseTeamMapping[]>(initialMappings ?? []);
  const [loading, setLoading] = useState(initialItems === undefined || initialMappings === undefined);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExerciseWalkthrough | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ExerciseWalkthrough | null>(null);

  const mappingById = useMemo(() => new Map(mappings.map((m) => [m.id, m])), [mappings]);

  useEffect(() => {
    if (initialItems !== undefined) setItems(initialItems);
  }, [initialItems]);

  useEffect(() => {
    if (initialMappings !== undefined) setMappings(initialMappings);
  }, [initialMappings]);

  useEffect(() => {
    // When the parent already fetched data for the scenario detail,
    // avoid extra network calls for walkthroughs and team mappings.
    if (initialItems !== undefined && initialMappings !== undefined) {
      setLoading(false);
      return;
    }
    let mounted = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const [walkthroughs, teamMappings] = await Promise.all([
          v2Api.dashboard.walkthroughs.listByExerciseId(exerciseId),
          v2Api.dashboard.exerciseTeamMappings.listByExerciseId(exerciseId),
        ]);
        if (!mounted) return;
        setItems(walkthroughs);
        setMappings(teamMappings);
      } catch (err: unknown) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load walkthrough data');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void run();
    return () => {
      mounted = false;
    };
  }, [exerciseId, initialItems, initialMappings]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, exercise_team: mappings[0]?.id ?? 0 });
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (item: ExerciseWalkthrough) => {
    setEditing(item);
    setForm({
      title: item.title || '',
      exercise_team: item.exercise_team ?? 0,
      is_active: item.is_active,
      file: null,
    });
    setError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setError(null);
  };

  const refreshData = async () => {
    if (onRefetch) {
      await onRefetch();
      return;
    }
    const [walkthroughs, teamMappings] = await Promise.all([
      v2Api.dashboard.walkthroughs.listByExerciseId(exerciseId),
      v2Api.dashboard.exerciseTeamMappings.listByExerciseId(exerciseId),
    ]);
    setItems(walkthroughs);
    setMappings(teamMappings);
  };

  const handleSave = async () => {
    if (!form.exercise_team) {
      setError('Please select an exercise-team mapping.');
      return;
    }
    if (!editing && !form.file) {
      setError('Please select a file.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editing) {
        await v2Api.dashboard.walkthroughs.update(exerciseId, editing.id, {
          title: form.title,
          exercise_team: form.exercise_team,
          is_active: form.is_active,
          ...(form.file ? { file: form.file } : {}),
        });
      } else {
        await v2Api.dashboard.walkthroughs.create(exerciseId, {
          title: form.title,
          exercise_team: form.exercise_team,
          is_active: form.is_active,
          file: form.file || undefined,
        });
      }
      closeDialog();
      await refreshData();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { detail?: string; errors?: Record<string, string[] | string> } } })?.response?.data;
      const fileErr = data?.errors?.file;
      const fileMessage = Array.isArray(fileErr) ? fileErr[0] : fileErr;
      const message =
        (typeof fileMessage === 'string' ? fileMessage : undefined) ??
        data?.detail ??
        (err instanceof Error ? err.message : 'Failed to save walkthrough');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (item: ExerciseWalkthrough) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setSaving(true);
    setError(null);
    try {
      await v2Api.dashboard.walkthroughs.delete(exerciseId, itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      await refreshData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        (err instanceof Error ? err.message : 'Failed to delete walkthrough');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-lg">{title}</CardTitle>
        <Button type="button" variant="default" size="sm" onClick={openCreate} disabled={mappings.length === 0}>
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Button>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-destructive mb-3">{error}</p>}
        {mappings.length === 0 && !loading && (
          <p className="text-xs text-muted-foreground mb-4">
            No exercise team mappings found. Create mappings first in the "Exercise Team Mappings" section.
          </p>
        )}
        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Loading walkthroughs...</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No walkthrough files yet.</p>
        ) : (
          <ul className="space-y-2">
              {items.map((item) => {
                const viewUrl = item.file_url || item.file;
                return (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-2 p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">
                        {item.title || item.file?.split('/').pop() || 'Untitled'}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span>{mappingById.get(item.exercise_team ?? 0)?.team_name ?? `Team ${item.team}`}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {viewUrl && (
                        <Button asChild type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={saving}>
                          <a href={viewUrl} target="_blank" rel="noopener noreferrer" aria-label="View walkthrough">
                            <Eye className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} disabled={saving}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => openDelete(item)}
                        disabled={saving}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                );
              })}
          </ul>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit walkthrough' : 'Add walkthrough'}</DialogTitle>
            <DialogDescription>Attach a walkthrough file for a team in this exercise.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="grid gap-2">
              <Label htmlFor="wt-title">Title</Label>
              <Input
                id="wt-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Optional title"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="wt-team">Exercise Team Mapping</Label>
              <Select
                id="wt-team"
                value={form.exercise_team ? String(form.exercise_team) : ''}
                onChange={(e) => setForm((f) => ({ ...f, exercise_team: parseInt(e.target.value, 10) || 0 }))}
              >
                <option value="">Select exercise team mapping</option>
                {mappings.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    {m.team_name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="wt-file">{editing ? 'Replace file (optional)' : 'File'}</Label>
              <Input
                id="wt-file"
                type="file"
                required={!editing}
                onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] ?? null }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="wt-active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="wt-active">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete walkthrough</DialogTitle>
            <DialogDescription>Delete this walkthrough file? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setItemToDelete(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={handleDeleteConfirm} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
